import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Users as UsersIcon, Shield, Eye, Wrench, Edit2, Trash2, Droplet, Plus } from 'lucide-react';
import { toast } from 'sonner';

const Users = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'water_monitor'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento degli utenti');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setIsCreating(true);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'water_monitor'
    });
    setDialogOpen(true);
  };

  const handleEditUser = (u) => {
    setIsCreating(false);
    setEditingUser(u);
    setFormData({
      name: u.name,
      email: u.email,
      password: '',
      role: u.role
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (isCreating) {
        // Create new user
        await axiosInstance.post('/auth/register', {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role
        });
        toast.success('Utente creato con successo!');
      } else {
        // Update existing user
        const updateData = {};
        
        // Include email only if changed
        if (formData.email !== editingUser.email) {
          updateData.email = formData.email;
        }
        
        // Include password only if provided
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        await axiosInstance.put(`/users/${editingUser.id}`, updateData);
        toast.success('Utente aggiornato con successo!');
      }
      
      fetchUsers();
      setDialogOpen(false);
      setEditingUser(null);
      setIsCreating(false);
      setFormData({ name: '', email: '', password: '', role: 'water_monitor' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Sei sicuro di voler eliminare l'utente ${userName}?`)) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/users/${userId}`);
      toast.success('Utente eliminato!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-5 h-5 text-red-600" />;
      case 'maintainer':
        return <Wrench className="w-5 h-5 text-blue-600" />;
      case 'viewer':
        return <Eye className="w-5 h-5 text-gray-600" />;
      case 'water_monitor':
        return <Droplet className="w-5 h-5 text-cyan-600" />;
      default:
        return <UsersIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return 'badge-red';
      case 'maintainer':
        return 'badge-blue';
      case 'viewer':
        return 'badge-yellow';
      case 'water_monitor':
        return 'badge-cyan';
      default:
        return 'badge-blue';
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Utenti</h1>
          <p className="text-gray-600">Gestione degli utenti del sistema</p>
        </div>
        {user?.role === 'admin' && (
          <Button
            onClick={handleCreateUser}
            className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi Utente
          </Button>
        )}
      </div>

      {/* Dialog per creazione/modifica utente */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isCreating ? 'Aggiungi Nuovo Utente' : 'Modifica Utente'}</DialogTitle>
            <DialogDescription>
              {isCreating 
                ? 'Crea un nuovo account utente (es: bagnino)'
                : `Modifica email e/o password per ${editingUser?.name}`
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {isCreating && (
              <div>
                <Label htmlFor="name">Nome Completo</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="es: Mario Rossi"
                  required
                />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@esempio.com"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">
                {isCreating ? 'Password' : 'Nuova Password (lascia vuoto per non cambiare)'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Inserisci password"
                required={isCreating}
              />
              {formData.password && formData.password.length < 8 && (
                <p className="text-xs text-red-600 mt-1">La password deve essere almeno 8 caratteri</p>
              )}
            </div>
            {isCreating && (
              <div>
                <Label htmlFor="role">Ruolo</Label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="water_monitor">Operatore Parametri Acqua (Bagnino)</option>
                  <option value="viewer">Visualizzatore</option>
                  <option value="maintainer">Manutentore</option>
                  <option value="admin">Amministratore</option>
                </select>
              </div>
            )}
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
              disabled={formData.password && formData.password.length < 8}
            >
              {isCreating ? 'Crea Utente' : 'Aggiorna'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.map((u) => (
          <Card key={u.id} className="card-hover shadow-lg border-0" data-testid={`user-card-${u.id}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center flex-1">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 mr-3">
                    {getRoleIcon(u.role)}
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg">{u.name}</CardTitle>
                    <p className="text-sm text-gray-600">{u.email}</p>
                  </div>
                </div>
                {user?.role === 'admin' && (
                  <div className="flex gap-2 ml-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditUser(u)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(u.id, u.name)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className={`badge ${getRoleBadge(u.role)}`}>
                  {u.role}
                </span>
                <p className="text-xs text-gray-500">
                  Dal {new Date(u.createdAt).toLocaleDateString('it-IT')}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Users;